import { Box } from "@mui/material";
import { tokens } from "../theme";

/**
 * A channel's mark: its own logo where it has one, a generated tile otherwise.
 *
 * ## Why this is a component and not three copies
 *
 * Channel artwork has bitten this repo twice for the same reason - somebody
 * drew a logo without using the one resolver that knows how. `ChannelsPage`
 * carried the first (it called `resolveVodThumbnailImage`, which ranks `logo`
 * LAST by design, so a channel WITH a logo drew its promo still - `BE-9`), and
 * the signage channel picker shipped the second: it read `images[].url`
 * straight into an `<img>`, but an upstream url is usually a STORAGE PATH, so
 * every tile in the picker was an empty grey box. Neither failure looks like an
 * error - `alt=""` means a broken image renders as nothing at all.
 *
 * The resolving is done where the data is (the server projections call
 * `resolveChannelLogoImage` + `imageSrc`); this owns how it is DRAWN, which is
 * the other half people get wrong.
 *
 * ## The plate is fixed white, on purpose
 *
 * Channel marks are overwhelmingly dark-on-transparent, and a good few are
 * white-on-transparent. On a themed surface either choice makes half the
 * line-up invisible, so the plate does not follow the theme - the artwork is
 * not theme-aware, and a logo nobody can see is worse than an ugly square.
 * `contain` with padding, never `cover`: a mark cropped to a square is no
 * longer the mark it exists to be.
 *
 * ## The fallback identifies, it does not apologise
 *
 * A channel with no artwork gets its initials on a colour derived from its id -
 * stable, so the same channel is the same colour on every surface and in every
 * session. A generic television glyph would make every unbranded channel look
 * identical, which in a picker is the one thing that must not happen.
 */
export interface ChannelMarkProps {
  /** Already through `imageSrc`. Null renders the initials tile. */
  src?: string | null;
  /** Used for the initials, so it should be what the operator reads. */
  name: string;
  /** Stable input for the fallback colour - the catalogue asset id. */
  id: string;
  /** Square edge in px. */
  size?: number;
}

export default function ChannelMark({
  src,
  name,
  id,
  size = 44,
}: ChannelMarkProps) {
  const common = {
    width: size,
    height: size,
    borderRadius: 1.5,
    flex: "0 0 auto",
  } as const;

  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt=""
        sx={{
          ...common,
          objectFit: "contain",
          backgroundColor: "#fff",
          padding: `${Math.max(2, Math.round(size * 0.09))}px`,
          border: `1px solid ${tokens.divider}`,
        }}
      />
    );
  }

  const hue = ([...id].reduce((n, c) => n + c.charCodeAt(0), 0) * 7) % 360;
  const initials =
    (name || "?")
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("") || "?";

  return (
    <Box
      aria-hidden
      sx={{
        ...common,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: Math.round(size * 0.33),
        fontWeight: 700,
        letterSpacing: 0.3,
        background: `linear-gradient(135deg, hsl(${hue} 55% 50%), hsl(${
          (hue + 35) % 360
        } 50% 36%))`,
      }}
    >
      {initials}
    </Box>
  );
}
